import { useEffect, useRef, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box } from '@mui/material';
import { MONO } from '../theme';

/** Monospace, virtualised list; sticks to the bottom while `follow` is on. */
export function VirtualLog<T>({
  items,
  render,
  follow,
  height = 480,
  estimate = 22,
  getKey,
  empty,
}: {
  items: T[];
  render: (item: T, index: number) => ReactNode;
  follow?: boolean;
  height?: number | string;
  estimate?: number;
  getKey?: (item: T, index: number) => string | number;
  empty?: ReactNode;
}) {
  const parent = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({
    count: items.length,
    getScrollElement: () => parent.current,
    estimateSize: () => estimate,
    overscan: 20,
    getItemKey: getKey ? (i) => getKey(items[i], i) : undefined,
  });

  useEffect(() => {
    if (follow && items.length) v.scrollToIndex(items.length - 1, { align: 'end' });
  }, [follow, items.length, v]);

  const virtual = v.getVirtualItems();
  // jsdom (tests) has no layout: render the first rows plainly.
  const fallback = virtual.length === 0 && items.length > 0;

  return (
    <Box
      ref={parent}
      role="log"
      aria-live={follow ? 'polite' : 'off'}
      sx={{
        height,
        overflow: 'auto',
        fontFamily: MONO,
        fontSize: 12.5,
        lineHeight: 1.6,
        bgcolor: (t) => (t.palette.mode === 'dark' ? '#0c0d0f' : '#fafbfc'),
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {items.length === 0 && empty}
      {fallback ? (
        items.slice(0, 200).map((it, i) => <div key={getKey ? getKey(it, i) : i}>{render(it, i)}</div>)
      ) : (
        <Box sx={{ height: v.getTotalSize(), position: 'relative', minWidth: '100%' }}>
          {virtual.map((row) => (
            <div key={row.key} data-index={row.index} ref={v.measureElement} style={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${row.start}px)` }}>
              {render(items[row.index], row.index)}
            </div>
          ))}
        </Box>
      )}
    </Box>
  );
}
